import { useEffect, useState, RefObject } from 'react'
import ListRow from '../../../common/ListRow'
import ListRowAction from '../../../common/ListRowAction'
import CopyIcon from '../../../assets/icons/copy'
import DeleteIcon from '../../../assets/icons/delete'
import FolderIcon from '../../../assets/icons/folder'
import AVCIcon from '../../../assets/icons/avc'
import { usePlayer } from '../../../provider/playerSource'
import { listRecords, getVideoToken, deleteRecord, play } from '../../../api/endpoints'
import { SourceType } from '../../../const/SourceType'
import PlayIcon from '../../../assets/icons/play'
import type { FolderListItem } from '@expostreamer/shared-types'
import type { InstanceViewHandle } from '../../../common/Modals/instanceView'
import styles from './style'

interface VodChannelProps {
  instanceViewModal: RefObject<InstanceViewHandle | null>
}

function VodChannel({ instanceViewModal }: VodChannelProps) {
  const { setVideoSource } = usePlayer()
  const [vodElements, setVodElements] = useState<FolderListItem[]>([])
  const [folderDepth, setFolderDepth] = useState<string[]>([])

  useEffect(() => {
    loadFolder()
  }, [folderDepth])

  function loadFolder() {
    const path = folderDepth.length ? folderDepth.join('/') + '/' : undefined
    listRecords(path).then(({ ok, data }) => {
      if (ok) setVodElements(data)
    })
  }

  const currentPath = folderDepth.length ? folderDepth.join('/') + '/' : ''

  // Same-named files on different drives are kept as separate rows (rather
  // than one shadowing the other) — label them with their drive so they're
  // distinguishable. Directories are already deduped by the backend.
  const fileNameCounts = vodElements.reduce<Record<string, number>>((counts, item) => {
    if (!item.isDir) counts[item.name] = (counts[item.name] ?? 0) + 1
    return counts
  }, {})

  return (
    <div style={styles.container}>
      {folderDepth.length > 0 ? (
        <ListRow
          leading={<FolderIcon size={28} />}
          label='Zurück'
          labelColor='#4caf50'
          onClick={() => {
            folderDepth.pop()
            setFolderDepth([...folderDepth])
          }}
        />
      ) : null}

      {vodElements.map(item => {
        const label =
          !item.isDir && fileNameCounts[item.name] > 1 && item.driveName
            ? `${item.name} (${item.driveName})`
            : item.name

        return (
          <ListRow
            key={item.name + (item.driveFolder ?? '')}
            leading={item.isDir ? <FolderIcon size={28} /> : <AVCIcon size={28} />}
            label={label}
            labelColor={item.isDir ? '#4caf50' : undefined}
            onClick={() => {
              if (item.isDir) {
                setFolderDepth(folder => [...folderDepth, item['name']])
              } else {
                getVideoToken(item.driveName, currentPath).then(({ ok, data }) => {
                  if (ok && data.videoToken) {
                    setVideoSource({
                      type: SourceType.FILE,
                      url:
                        './records/video?name=' +
                        item['name'] +
                        '&drive=' +
                        encodeURIComponent(item.driveName ?? '') +
                        '&folder=' +
                        encodeURIComponent(currentPath) +
                        '&videoToken=' +
                        data.videoToken,
                    })
                  } else {
                    window.location.href = './login'
                  }
                })
              }
            }}
            actions={
              !item.isDir ? (
                <>
                  <ListRowAction
                    onClick={() => {
                      getVideoToken(item.driveName, currentPath).then(async ({ ok, data }) => {
                        if (ok && data.videoToken) {
                          await navigator.clipboard.writeText(
                            'https://expostreamer.com/records/video?name=' +
                              item['name'] +
                              '&drive=' +
                              encodeURIComponent(item.driveName ?? '') +
                              '&folder=' +
                              encodeURIComponent(currentPath) +
                              '&videoToken=' +
                              data.videoToken
                          )
                        } else {
                          window.location.href = './login'
                        }
                      })
                    }}
                  >
                    <CopyIcon size={20} />
                  </ListRowAction>
                  <ListRowAction
                    onClick={() => {
                      if (window.confirm('Wirklich löschen?')) {
                        deleteRecord(
                          'name=' +
                            item['name'] +
                            '&drive=' +
                            encodeURIComponent(item.driveName ?? '') +
                            '&folder=' +
                            encodeURIComponent(currentPath)
                        ).then(({ status }) => {
                          if (status === 200) {
                            console.log('Datei gelöscht')
                            loadFolder()
                          }
                        })
                      }
                    }}
                  >
                    <DeleteIcon
                      size={20}
                      color='#e05d5d'
                    />
                  </ListRowAction>
                  <ListRowAction
                    onClick={() => {
                      instanceViewModal?.current?.show(preset => {
                        play({
                          videoSource: item.driveFolder! + currentPath + item['name'],
                          presetName: preset.name,
                        })
                      })
                    }}
                  >
                    <PlayIcon size={20} />
                  </ListRowAction>
                </>
              ) : undefined
            }
          />
        )
      })}
    </div>
  )
}

export default VodChannel
